/**
 * Product Form Customizations
 * Consolidated customizations for Shopify product forms
 * 
 * Features:
 * - Metafield-based variant swatches (images, colors, color pairs)
 * - Read More/Less for product descriptions
 * - Accordion UI for warranty sections
 * 
 * Theme: Focal by Maestrooo
 */

(function () {
    'use strict';

    /* ========================================================================
       VARIANT SWATCH SELECTOR
       Extends theme's color-swatch system with metafield support
       ======================================================================== */

    const SWATCH_CONFIG = {
        metafields: {
            image: 'swatch_image',
            color1: 'swatch_color_1',
            color2: 'swatch_color_2'
        },
        selectors: {
            // Product pages
            productOption: '.product-form__option-selector',
            // Collection pages - both theme native color swatches and metafield swatches
            collectionSwatchList: '.color-swatch-list, .block-swatch-list',
            radioInput: 'input[type="radio"]',
            variantContainer: '.variant-radios, .block-swatch-list',
            swatchContainer: '.color-swatch-list',
            quickBuyButton: 'button[aria-controls*="quick"]',
            quickBuyDrawer: 'quick-buy-drawer',
            quickBuyPopover: 'quick-buy-popover'
        },
        classes: {
            // Use theme's color-swatch classes
            swatchWrapper: 'color-swatch',
            swatchItem: 'color-swatch__item',
            swatchRadio: 'color-swatch__radio',
            swatchImage: 'color-swatch__item--image',
            swatchColor: 'color-swatch__item--color',
            swatchColorPair: 'color-swatch__item--color-pair',
            whiteModifier: 'color-swatch--white',
            tooltip: 'color-swatch__tooltip'
        }
    };

    class VariantSwatchSelector {
        constructor(container) {
            this.container = container;
            this.optionName = container.dataset.optionName;
            this.optionIndex = container.dataset.optionIndex;
            this.init();
        }

        init() {
            this.createSwatches();
            this.setupEventListeners();
            this.updateSelectedState();
        }

        createSwatches() {
            const radioInputs = this.container.querySelectorAll(SWATCH_CONFIG.selectors.radioInput);
            if (!radioInputs.length) return;

            // Check if ALL variants have swatch data (all-or-nothing approach)
            const variantData = [];
            const missingVariants = [];
            const originalParents = [];

            radioInputs.forEach((radio) => {
                const swatchData = this.getSwatchData(radio);
                if (swatchData) {
                    variantData.push({ radio, swatchData });
                    originalParents.push(radio.parentElement);
                } else {
                    missingVariants.push(radio.value);
                }
            });

            // If any variants are missing swatch data, don't create swatches at all
            if (missingVariants.length > 0) {
                // console.warn(
                //     `⚠️ Variant swatches disabled for "${this.optionName}" - ` +
                //     `${missingVariants.length} of ${radioInputs.length} variant(s) missing swatch metafield data:\n` +
                //     `Missing: ${missingVariants.join(', ')}\n` +
                //     `To enable swatches, add swatch_image, swatch_color_1, or swatch_color_2 metafields to all variants.`
                // );
                return;
            }

            // All variants have swatch data - proceed with creation
            // Use theme's .color-swatch-list container
            const swatchContainer = document.createElement('div');
            swatchContainer.className = 'color-swatch-list';

            variantData.forEach(({ radio, swatchData }) => {
                const swatch = this.createSwatch(radio, swatchData);
                if (swatch) {
                    swatchContainer.appendChild(swatch);
                }
            });

            // Insert swatches and hide original controls
            this.container.appendChild(swatchContainer);
            const optionSelector = this.container.closest(SWATCH_CONFIG.selectors.productOption);
            if (optionSelector) {
                optionSelector.dataset.selectorType = 'color';
            }

            // Remove the now-empty original wrapper elements (their radio input was
            // moved into the new swatch markup above, leaving a stale label behind)
            originalParents.forEach((parent) => {
                if (parent && parent !== this.container && parent.parentElement) {
                    parent.remove();
                }
            });

            // Hide all original block-swatch wrappers (the text button containers)
            const originalSwatches = this.container.querySelectorAll('.block-swatch, .variant-radios > fieldset, .variant-radios > label');
            originalSwatches.forEach(element => {
                element.style.display = 'none';
            });
        }

        getSwatchData(radio) {
            const value = radio.value;

            // First check if data attributes are on the radio input itself
            const hasDataOnRadio = radio.dataset.swatchImage || radio.dataset['swatchColor-1'] || radio.dataset['swatchColor-2'];

            if (hasDataOnRadio) {
                return {
                    image: radio.dataset.swatchImage,
                    color1: radio.dataset['swatchColor-1'],
                    color2: radio.dataset['swatchColor-2'],
                    value: value
                };
            }

            // Fallback: check parent elements
            const optionElement = radio.closest('[data-swatch-image], [data-swatch-color-1], [data-swatch-color-2]');

            if (!optionElement) {
                // Try to get from variant data if available
                return this.getSwatchFromVariantData(value);
            }

            return {
                image: optionElement.dataset.swatchImage,
                color1: optionElement.dataset['swatchColor-1'],
                color2: optionElement.dataset['swatchColor-2'],
                value: value
            };
        }

        getSwatchFromVariantData(value) {
            // This would be populated by Liquid template with variant metafields
            if (window.variantSwatchData && window.variantSwatchData[this.optionName]) {
                return window.variantSwatchData[this.optionName][value];
            }
            return null;
        }

        createSwatch(radio, data) {
            // Create wrapper div with theme's .color-swatch class
            const wrapper = document.createElement('div');
            wrapper.className = SWATCH_CONFIG.classes.swatchWrapper;

            // Check if white color for modifier class
            const isWhite = data.color1 && this.isWhiteColor(data.color1);
            if (isWhite) {
                wrapper.classList.add(SWATCH_CONFIG.classes.whiteModifier);
            }

            // Move the radio into the wrapper and add theme class
            // Remove any existing swatch classes and add only color-swatch__radio
            radio.className = 'visually-hidden';
            radio.classList.add(SWATCH_CONFIG.classes.swatchRadio);

            // Create label (theme uses label as the clickable item)
            const label = document.createElement('label');
            label.className = SWATCH_CONFIG.classes.swatchItem;
            label.htmlFor = radio.id;
            label.dataset.value = data.value;
            label.setAttribute('aria-label', data.value);

            // Determine swatch type and apply styling
            if (data.image) {
                this.applyImageSwatch(label, data.image);
            } else if (data.color1 && data.color2) {
                this.applyColorPairSwatch(label, data.color1, data.color2);
            } else if (data.color1) {
                this.applyColorSwatch(label, data.color1);
            } else {
                return null; // No valid swatch data
            }

            // Add tooltip
            const tooltip = document.createElement('span');
            tooltip.className = SWATCH_CONFIG.classes.tooltip;
            tooltip.textContent = data.value;

            // IMPORTANT: Structure must be:
            // 1. radio (for CSS selector .color-swatch__radio:checked + .color-swatch__item)
            // 2. label (must be immediately after radio)
            // 3. tooltip (can come after)
            wrapper.appendChild(radio);
            wrapper.appendChild(label);
            wrapper.appendChild(tooltip);

            return wrapper;
        }

        isWhiteColor(color) {
            const normalized = color.toLowerCase().trim();
            return normalized === '#fff' ||
                normalized === '#ffffff' ||
                normalized === 'white' ||
                normalized === 'rgb(255, 255, 255)' ||
                normalized === 'rgba(255, 255, 255, 1)';
        }

        applyImageSwatch(label, imageUrl) {
            label.classList.add(SWATCH_CONFIG.classes.swatchImage);
            label.style.backgroundImage = `url(${imageUrl})`;
        }

        applyColorSwatch(label, color) {
            label.classList.add(SWATCH_CONFIG.classes.swatchColor);
            label.style.backgroundColor = color;
        }

        applyColorPairSwatch(label, color1, color2) {
            label.classList.add(SWATCH_CONFIG.classes.swatchColorPair);
            label.style.setProperty('--swatch-color-1', color1);
            label.style.setProperty('--swatch-color-2', color2);
        }

        setupEventListeners() {
            // Theme uses label.color-swatch__item as clickable element
            const swatches = this.container.querySelectorAll(`.${SWATCH_CONFIG.classes.swatchItem}`);

            swatches.forEach((label) => {
                // Keyboard support for labels
                label.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        label.click(); // Trigger the native label click
                    }
                });
            });

            // Listen for radio changes (labels handle clicks natively)
            const radios = this.container.querySelectorAll(`.${SWATCH_CONFIG.classes.swatchRadio}`);
            radios.forEach((radio) => {
                radio.addEventListener('change', () => {
                    this.updateSelectedState();
                    this.updateQuickViewUrls();

                    // Dispatch custom event
                    const customEvent = new CustomEvent('swatch:selected', {
                        detail: {
                            value: radio.value,
                            optionName: this.optionName,
                            optionIndex: this.optionIndex,
                            variantId: radio.dataset.variantId
                        },
                        bubbles: true
                    });
                    document.dispatchEvent(customEvent);
                });
            });

            // Listen for variant changes from other sources
            document.addEventListener('variant:changed', () => {
                this.updateSelectedState();
            });
        }

        updateSelectedState() {
            // Theme uses ::after pseudo-element for selected state
            // The radio:checked + label CSS selector automatically handles visual state
            // We just need to handle disabled states
            const radios = this.container.querySelectorAll(`.${SWATCH_CONFIG.classes.swatchRadio}`);

            radios.forEach((radio) => {
                const wrapper = radio.closest(`.${SWATCH_CONFIG.classes.swatchWrapper}`);
                if (wrapper) {
                    // Theme handles disabled with .is-disabled class on wrapper
                    if (radio.disabled) {
                        wrapper.classList.add('is-disabled');
                    } else {
                        wrapper.classList.remove('is-disabled');
                    }
                }
            });
        }

        updateQuickViewUrls() {
            // Find the product item container
            const productItem = this.container.closest('product-item');
            if (!productItem) return;

            // Get all selected variant IDs from all options
            const selectedVariants = productItem.querySelectorAll(`${SWATCH_CONFIG.selectors.radioInput}:checked`);
            const variantIds = Array.from(selectedVariants)
                .map(radio => radio.dataset.variantId)
                .filter(Boolean);

            // If we have variant selections, update Quick View URLs
            if (variantIds.length > 0) {
                const primaryVariantId = variantIds[0]; // Use first selected variant

                // Update quick-buy-drawer and quick-buy-popover href attributes
                const quickBuyElements = productItem.querySelectorAll(
                    `${SWATCH_CONFIG.selectors.quickBuyDrawer}, ${SWATCH_CONFIG.selectors.quickBuyPopover}`
                );

                quickBuyElements.forEach(element => {
                    const currentHref = element.getAttribute('href');
                    if (currentHref) {
                        // Add or update variant parameter in URL
                        const url = new URL(currentHref, window.location.origin);
                        url.searchParams.set('variant', primaryVariantId);
                        const newHref = url.pathname + url.search;
                        element.setAttribute('href', newHref);
                    }
                });
            }
        }
    }

    /* ========================================================================
       READ MORE FUNCTIONALITY
       Truncates long product descriptions with fade effect
       ======================================================================== */

    const READ_MORE_CONFIG = {
        selectors: {
            container: '.product-form__text',
            contentWrapper: 'metafield-multi_line_text_field'
        },
        maxLines: {
            desktop: 4,  // Show first 4 lines when collapsed
            mobile: 3    // Show first 3 lines on mobile
        },
        minLines: 4,  // Don't show Read More unless content is at least this many lines tall (must be > maxLines)
        toggleText: {
            more: 'Read more',
            less: 'Read less'
        }
    };

    class ReadMoreHandler {
        constructor(container) {
            this.container = container;
            this.content = container.querySelector(`.${READ_MORE_CONFIG.selectors.contentWrapper}`);

            if (!this.content) return;

            this.originalHeight = null;
            this.isExpanded = false;
            this.toggleButton = null;

            this.init();
        }

        init() {
            // Get the raw content - might be HTML-encoded by Shopify
            const rawContent = this.content.innerHTML;

            // Check if content contains HTML entities (encoded HTML)
            const hasHTMLEntities = /&lt;|&gt;|&amp;/.test(rawContent);

            if (hasHTMLEntities) {
                // Decode HTML entities so tags render properly
                const decoded = this.decodeHTML(rawContent);
                this.originalHTML = decoded;
                // console.log('Read More: HTML entities detected and decoded');
            } else {
                // Content is already proper HTML or plain text
                this.originalHTML = rawContent;
            }

            // Set the decoded content
            this.content.innerHTML = this.originalHTML;

            // Check if content contains "no-read-more" class as a complete class name
            // Match patterns like class="no-read-more" or class="foo no-read-more bar"
            // This works with both encoded (&lt;div class=&quot;no-read-more&quot;&gt;) and plain HTML
            if (/class=["']([^"']*\s)?no-read-more(\s[^"']*)?["']/.test(rawContent)) {
                console.log('Skipping read more - no-read-more class found in content');
                return;
            }

            // Decide whether the content overflows the collapsed clamp box by
            // applying the SAME line-clamp the UI uses and asking the browser
            // directly: does it overflow? Comparing scrollHeight to clientHeight
            // uses one rendering context for both values, so it is immune to the
            // cross-platform font-metric differences (e.g. Chrome on macOS) that
            // made pixel/line-height math falsely trigger on short descriptions.
            const maxLines = this.getMaxLines();

            this.container.classList.add('read-more-container', 'is-collapsed');
            this.content.classList.add('read-more-content');
            this.content.style.setProperty('--read-more-lines', maxLines);

            // Force a reflow so the clamp is applied before measuring.
            this.content.offsetHeight;

            // +1px tolerance to absorb sub-pixel rounding.
            const isOverflowing =
                this.content.scrollHeight > this.content.clientHeight + 1;

            // Remove the temporary measurement state; setupReadMore re-applies it.
            this.container.classList.remove('read-more-container', 'is-collapsed');
            this.content.classList.remove('read-more-content');
            this.content.style.removeProperty('--read-more-lines');

            // Only add Read More when the content genuinely exceeds the clamp.
            if (!isOverflowing) {
                return;
            }

            this.setupReadMore();
        }

        decodeHTML(html) {
            // Create a temporary element to decode HTML entities
            const txt = document.createElement('textarea');
            txt.innerHTML = html;
            return txt.value;
        }

        getMaxLines() {
            return this.isMobile() ? READ_MORE_CONFIG.maxLines.mobile : READ_MORE_CONFIG.maxLines.desktop;
        }

        isMobile() {
            return window.innerWidth < 768;
        }

        setupReadMore() {
            // Clean up any existing read more elements first
            this.cleanup();

            // Add wrapper class for styling
            this.container.classList.add('read-more-container');
            this.container.classList.add('is-collapsed');

            // Ensure original HTML is displayed
            this.content.innerHTML = this.originalHTML;

            // Add truncation class to content wrapper
            this.content.classList.add('read-more-content');

            // Apply line-clamp via CSS custom property
            const maxLines = this.getMaxLines();
            this.content.style.setProperty('--read-more-lines', maxLines);

            // Create toggle link
            this.toggleButton = document.createElement('a');
            this.toggleButton.className = 'read-more-toggle';
            this.toggleButton.href = '#';
            this.toggleButton.textContent = READ_MORE_CONFIG.toggleText.more;
            this.toggleButton.setAttribute('aria-expanded', 'false');
            this.toggleButton.setAttribute('role', 'button');

            this.toggleButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggle();
            });

            // Insert button after content
            this.content.parentNode.insertBefore(this.toggleButton, this.content.nextSibling);

            // console.log(`Read More: setup complete, button inserted, truncated to ${maxLines} lines`);
        }

        cleanup() {
            // Remove any existing toggle buttons
            const existingButtons = this.container.querySelectorAll('.read-more-toggle');
            existingButtons.forEach(button => button.remove());

            // Remove classes
            this.container.classList.remove('read-more-container', 'is-collapsed', 'is-expanded');
            if (this.content) {
                this.content.classList.remove('read-more-content');
                this.content.style.removeProperty('--read-more-lines');
            }

            // Restore original content if it exists
            if (this.originalHTML && this.content) {
                this.content.innerHTML = this.originalHTML;
            }
        }

        toggle() {
            this.isExpanded = !this.isExpanded;

            if (this.isExpanded) {
                // Expand - remove line clamp
                this.container.classList.remove('is-collapsed');
                this.container.classList.add('is-expanded');
                this.toggleButton.textContent = READ_MORE_CONFIG.toggleText.less;
                this.toggleButton.setAttribute('aria-expanded', 'true');
            } else {
                // Collapse - apply line clamp
                this.container.classList.remove('is-expanded');
                this.container.classList.add('is-collapsed');
                this.toggleButton.textContent = READ_MORE_CONFIG.toggleText.more;
                this.toggleButton.setAttribute('aria-expanded', 'false');

                // Scroll back to top of content
                this.container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }

    /* ========================================================================
       WARRANTY ACCORDION FUNCTIONALITY
       Collapsible warranty sections with +/- icons
       ======================================================================== */

    const ACCORDION_CONFIG = {
        selectors: {
            warrantyContainer: '.wk_default_warranty',
            accordionTriggers: [
                {
                    section: '.wk_extend_warranty_part',
                    trigger: '.wk_extend_warranty_available',
                    content: '.wk_extend_all_warranty'
                },
                {
                    section: '.wk_damage_warranty_part',
                    trigger: '.wk_extend_warranty_available',
                    content: '.wk_damage_all_warranty'
                }
            ]
        },
        icons: {
            collapsed: '+',
            expanded: '−' // Using minus sign (U+2212)
        },
        startCollapsed: true
    };

    class WarrantyAccordion {
        constructor(container) {
            this.container = container;
            this.accordions = [];
            this.init();
        }

        init() {
            // console.log('WarrantyAccordion: Initializing accordions...');
            ACCORDION_CONFIG.selectors.accordionTriggers.forEach((config, index) => {
                // console.log(`Accordion ${index}: section="${config.section}"`);
                const section = this.container.querySelector(config.section);
                if (section) {
                    const trigger = section.querySelector(config.trigger);
                    // Content is the next sibling element, not inside the section
                    const content = section.nextElementSibling;
                    const contentMatches = content && content.matches(config.content);
                    // console.log(`  → section found, trigger: ${!!trigger}, content sibling: ${!!content}, matches "${config.content}": ${contentMatches}`);
                    if (trigger && content && contentMatches) {
                        this.setupAccordion(section, trigger, content, index);
                        // console.log(`  ✅ Accordion ${index} setup complete`);
                    } else {
                        // console.log(`  ⚠️ Accordion ${index} NOT setup (missing trigger or content)`);
                    }
                } else {
                    // console.log(`  ⚠️ Accordion ${index} NOT setup (section not found)`);
                }
            });
        }

        setupAccordion(section, trigger, content, index) {
            // Add warranty-accordion class to the existing section
            section.classList.add('warranty-accordion');

            // Add classes for styling
            trigger.classList.add('warranty-accordion__trigger');
            content.classList.add('warranty-accordion__content');

            // Create icon element
            const icon = document.createElement('span');
            icon.className = 'warranty-accordion__icon';
            icon.textContent = ACCORDION_CONFIG.icons.collapsed;
            icon.setAttribute('aria-hidden', 'true');

            // Insert icon at the end of trigger (right side)
            trigger.appendChild(icon);

            // Make trigger interactive
            trigger.setAttribute('role', 'button');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.setAttribute('aria-controls', `warranty-accordion-${index}`);
            trigger.setAttribute('tabindex', '0');

            // Set content ID for ARIA
            content.setAttribute('id', `warranty-accordion-${index}`);

            // Set initial collapsed state
            if (ACCORDION_CONFIG.startCollapsed) {
                content.style.display = 'none';
                section.classList.add('is-collapsed');
            } else {
                section.classList.add('is-expanded');
                icon.textContent = ACCORDION_CONFIG.icons.expanded;
                trigger.setAttribute('aria-expanded', 'true');
            }

            // Add click event
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleAccordion(section, trigger, content, icon);
            });

            // Add keyboard support
            trigger.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.toggleAccordion(section, trigger, content, icon);
                }
            });

            this.accordions.push({ section, trigger, content, icon });
        }

        toggleAccordion(section, trigger, content, icon) {
            const isExpanded = section.classList.contains('is-expanded');

            if (isExpanded) {
                // Collapse
                section.classList.remove('is-expanded');
                section.classList.add('is-collapsed');
                content.style.display = 'none';
                icon.textContent = ACCORDION_CONFIG.icons.collapsed;
                trigger.setAttribute('aria-expanded', 'false');
            } else {
                // Expand
                section.classList.remove('is-collapsed');
                section.classList.add('is-expanded');
                content.style.display = 'block';
                icon.textContent = ACCORDION_CONFIG.icons.expanded;
                trigger.setAttribute('aria-expanded', 'true');
            }
        }
    }

    /* ========================================================================
       INITIALIZATION
       Initialize all product form customizations
       ======================================================================== */

    function initializeSwatches() {
        // Product pages - look for .product-form__option-selector
        const productContainers = document.querySelectorAll(SWATCH_CONFIG.selectors.productOption);
        // console.log(`ProductFormCustomizations: Found ${productContainers.length} product option containers`);

        productContainers.forEach((container) => {
            // Check if this container has a .block-swatch-list inside (for "block" selector type)
            const blockSwatchList = container.querySelector('.block-swatch-list');
            if (blockSwatchList) {
                // Copy data attributes from parent to .block-swatch-list for initialization
                blockSwatchList.dataset.optionName = container.dataset.optionName;
                blockSwatchList.dataset.optionIndex = container.dataset.optionIndex;
                // Initialize the .block-swatch-list instead of the parent
                if (!blockSwatchList.dataset.swatchInitialized) {
                    new VariantSwatchSelector(blockSwatchList);
                    blockSwatchList.dataset.swatchInitialized = 'true';
                }
                container.dataset.swatchInitialized = 'true';
            } else if (!container.dataset.swatchInitialized) {
                // Initialize the parent container (for other selector types)
                new VariantSwatchSelector(container);
                container.dataset.swatchInitialized = 'true';
            }
        });

        // Collection pages - look for .block-swatch-list (metafield swatches)
        const collectionContainers = document.querySelectorAll(SWATCH_CONFIG.selectors.collectionSwatchList);

        collectionContainers.forEach((container) => {
            if (!container.dataset.swatchInitialized && container.dataset.optionName) {
                new VariantSwatchSelector(container);
                container.dataset.swatchInitialized = 'true';
            }
        });
    }

    function initializeReadMore() {
        const descriptionContainers = document.querySelectorAll(READ_MORE_CONFIG.selectors.container);
        // console.log(`ProductFormCustomizations: Found ${descriptionContainers.length} description containers for Read More`);
        descriptionContainers.forEach(container => {
            if (!container.dataset.readMoreInitialized) {
                new ReadMoreHandler(container);
                container.dataset.readMoreInitialized = 'true';
            }
        });
    }

    function initializeWarrantyAccordions() {
        const warrantyContainers = document.querySelectorAll(ACCORDION_CONFIG.selectors.warrantyContainer);
        // console.log(`ProductFormCustomizations: Found ${warrantyContainers.length} warranty containers for accordions`);

        warrantyContainers.forEach(container => {
            if (!container.dataset.accordionInitialized) {
                // Check if warranty widget has loaded content yet
                const hasSections = container.querySelector('.wk_extend_warranty_part, .wk_damage_warranty_part');

                if (hasSections) {
                    // Content already loaded, initialize now
                    new WarrantyAccordion(container);
                    container.dataset.accordionInitialized = 'true';
                } else {
                    // Content not loaded yet, watch for it
                    // console.log('Warranty widget content not loaded yet, watching for changes...');
                    const observer = new MutationObserver((mutations) => {
                        const nowHasSections = container.querySelector('.wk_extend_warranty_part, .wk_damage_warranty_part');
                        if (nowHasSections && !container.dataset.accordionInitialized) {
                            // console.log('Warranty widget content detected, initializing accordions...');
                            new WarrantyAccordion(container);
                            container.dataset.accordionInitialized = 'true';
                            observer.disconnect();
                        }
                    });

                    observer.observe(container, {
                        childList: true,
                        subtree: true
                    });

                    // Also try again after a delay as backup
                    setTimeout(() => {
                        if (!container.dataset.accordionInitialized) {
                            const nowHasSections = container.querySelector('.wk_extend_warranty_part, .wk_damage_warranty_part');
                            if (nowHasSections) {
                                // console.log('Warranty widget content found on retry, initializing accordions...');
                                new WarrantyAccordion(container);
                                container.dataset.accordionInitialized = 'true';
                            }
                        }
                    }, 2000);
                }
            }
        });
    }

    function initializeAll() {
        // console.log('ProductFormCustomizations v3.1.1: Initializing all features...');
        initializeSwatches();
        initializeReadMore();
        initializeWarrantyAccordions();
        // console.log('ProductFormCustomizations v3.1.1: Initialization complete');
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeAll);
    } else {
        initializeAll();
    }

    // Re-initialize on dynamic content load (for quick view, AJAX, mini-cart, etc.)
    document.addEventListener('shopify:section:load', initializeAll);
    document.addEventListener('product:loaded', initializeAll);
    document.addEventListener('quick-buy:loaded', initializeAll);
    document.addEventListener('drawer:open', initializeAll);
    document.addEventListener('popover:open', initializeAll);

    // Watch for Quick View opening (custom elements)
    document.body.addEventListener('open', (e) => {
        if (e.target.tagName === 'QUICK-BUY-DRAWER' || e.target.tagName === 'QUICK-BUY-POPOVER') {
            setTimeout(() => {
                const quickBuyContainer = e.target;
                const containers = quickBuyContainer.querySelectorAll('.product-form__option-selector, .block-swatch-list');
                containers.forEach(container => {
                    delete container.dataset.swatchInitialized;
                });
                initializeAll();
            }, 50);
        }
    }, true);

    // Watch for details element opening (drawer might use this)
    document.body.addEventListener('toggle', (e) => {
        const target = e.target;
        if (target.open && (target.classList.contains('drawer') || target.classList.contains('popover'))) {
            setTimeout(() => {
                const containers = target.querySelectorAll('.product-form__option-selector, .block-swatch-list');
                containers.forEach(container => {
                    delete container.dataset.swatchInitialized;
                });
                initializeAll();
            }, 50);
        }
    }, true);

    // Watch for dynamic content additions
    let observerTimeout;
    const observer = new MutationObserver((mutations) => {
        clearTimeout(observerTimeout);
        observerTimeout = setTimeout(() => {
            let shouldReinitialize = false;

            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length) {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1) {
                            const isQuickViewContent = node.classList && (
                                node.classList.contains('drawer__inner') ||
                                node.classList.contains('popover__inner') ||
                                node.closest('quick-buy-drawer, quick-buy-popover')
                            );

                            if (isQuickViewContent) {
                                shouldReinitialize = true;
                            }

                            const containers = node.querySelectorAll ?
                                node.querySelectorAll(SWATCH_CONFIG.selectors.productOption) : [];
                            if (containers.length > 0) {
                                for (let container of containers) {
                                    if (!container.dataset.swatchInitialized) {
                                        shouldReinitialize = true;
                                        break;
                                    }
                                }
                            }
                        }
                    });
                }
            });

            if (shouldReinitialize) {
                initializeAll();
            }
        }, 100);
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Handle resize for read more (recalculate on mobile/desktop switch)
    let resizeTimeout;
    let lastIsMobile = window.innerWidth < 768;

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            const currentIsMobile = window.innerWidth < 768;

            // Only reinitialize if we've crossed the mobile/desktop breakpoint
            if (currentIsMobile !== lastIsMobile) {
                lastIsMobile = currentIsMobile;

                document.querySelectorAll('[data-read-more-initialized]').forEach(el => {
                    delete el.dataset.readMoreInitialized;
                });
                initializeReadMore();
            }
        }, 250);
    });

    // Expose for external access
    window.ProductFormCustomizations = {
        VariantSwatchSelector,
        ReadMoreHandler,
        WarrantyAccordion,
        initialize: initializeAll,
        initializeSwatches,
        initializeReadMore,
        initializeWarrantyAccordions
    };
})();
